import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Globe, CheckCircle, Warning, Copy } from "@phosphor-icons/react";

export default function CustomDomain() {
  const { user, refreshMe } = useAuth();
  const [domain, setDomain] = useState(user?.custom_domain || "");
  const [verified, setVerified] = useState(!!user?.custom_domain_verified);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(""); setOk("");
    try {
      const { data } = await api.put("/profile/custom-domain", { custom_domain: domain.trim() || null });
      setVerified(!!data.verified);
      setToken(data.verification_token || "");
      setOk(domain ? "Domain saved. Add the DNS records, then verify." : "Custom domain removed.");
      await refreshMe();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Failed to save");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true); setErr(""); setOk("");
    try {
      const { data } = await api.post("/profile/custom-domain/verify");
      setVerified(!!data.verified);
      setOk("Domain verified.");
      await refreshMe();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Verification failed");
    } finally { setBusy(false); }
  };

  const copy = (text) => navigator.clipboard?.writeText(text);

  return (
    <div>
      <div className="mb-8">
        <div className="eyebrow text-muted-foreground mb-2">Domain</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-black tracking-tight">Custom domain</h1>
        <p className="mt-2 text-sm text-muted-foreground">Bring your own domain — keep your URL, change the address.</p>
      </div>

      <div className="grid grid-cols-12 gap-8">
        <form onSubmit={submit} className="col-span-12 lg:col-span-7 space-y-6" data-testid="custom-domain-form">
          <div>
            <Label className="eyebrow text-muted-foreground">Your domain</Label>
            <div className="mt-2 flex items-stretch border border-border bg-card">
              <span className="px-3 text-sm text-muted-foreground border-r border-border grid place-items-center">https://</span>
              <Input
                value={domain}
                onChange={(e) => setDomain(e.target.value.toLowerCase())}
                className="rounded-none border-0 h-12 font-mono"
                placeholder="blog.yoursite.com"
                data-testid="custom-domain-input"
              />
              <div className="px-3 grid place-items-center">
                {verified ? (
                  <CheckCircle size={18} weight="duotone" className="text-emerald-600" />
                ) : domain ? (
                  <Warning size={18} weight="duotone" className="text-amber-600" />
                ) : null}
              </div>
            </div>
            <div className="text-xs text-muted-foreground mt-1">Your blog is currently public at <span className="font-mono text-foreground">/{user?.username}</span></div>
          </div>

          {err ? <div className="text-sm text-destructive" data-testid="custom-domain-error">{err}</div> : null}
          {ok ? <div className="text-sm text-emerald-700 dark:text-emerald-400">{ok}</div> : null}

          <div className="flex items-center gap-3">
            <Button type="submit" disabled={busy} className="rounded-none bg-foreground text-background hover:bg-foreground/90" data-testid="custom-domain-save">
              {busy ? "Saving..." : "Save domain"}
            </Button>
            {user?.custom_domain && !verified ? (
              <Button type="button" onClick={verify} variant="outline" className="rounded-none border-foreground" data-testid="custom-domain-verify">
                Verify DNS
              </Button>
            ) : null}
          </div>
        </form>

        <aside className="col-span-12 lg:col-span-5">
          <div className="border border-border bg-card p-6 space-y-4">
            <div className="flex items-center gap-2">
              <Globe size={18} weight="duotone" />
              <div className="font-serif text-xl font-bold">DNS Setup</div>
            </div>
            <p className="text-sm text-muted-foreground">After saving your domain, add these records at your DNS provider (Cloudflare, Namecheap, etc):</p>
            <div className="space-y-3">
              <div className="border border-border p-3">
                <div className="eyebrow text-muted-foreground mb-1">1. CNAME record</div>
                <div className="text-xs font-mono break-all flex items-center justify-between gap-2">
                  <span>{domain || "your-domain.com"} → inkspot-platform.example.com</span>
                  <button type="button" onClick={() => copy("inkspot-platform.example.com")} className="text-muted-foreground hover:text-foreground" aria-label="Copy">
                    <Copy size={12} />
                  </button>
                </div>
              </div>
              {(token || (user?.custom_domain && !verified)) ? (
                <div className="border border-border p-3">
                  <div className="eyebrow text-muted-foreground mb-1">2. TXT record (verification)</div>
                  <div className="text-xs font-mono break-all flex items-center justify-between gap-2">
                    <span>{token || "Save your domain first to generate a token"}</span>
                    {token ? (
                      <button type="button" onClick={() => copy(token)} className="text-muted-foreground hover:text-foreground" aria-label="Copy">
                        <Copy size={12} />
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
            <div className="text-xs text-muted-foreground">
              DNS changes usually propagate in 5-30 min. Click Verify after that.
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
